/**
 * Agremo Models (Admin)
 *
 * Interfaces for Agremo credit management and analysis views in admin portal.
 * Matches backend data models from hub/src/data-models/agremo-*.ts
 */

// ============================================
// CREDIT BLOCK
// ============================================

/**
 * Native unit of an Agremo credit block (2026-05-20 multi-unit design).
 *  - 'analysis': legacy "1 credit = 1 analysis" rows. Read-only — drains
 *    out as historical data; cannot be created from the admin UI.
 *  - 'acres'   : per-acre accounting; preferred new default.
 *  - 'hectares': Agremo-native unit; converts to acres at consume time
 *    using 1 ha = 2.47105 acres.
 */
export type AgremoCreditType = 'analysis' | 'acres' | 'hectares';

/** Acres per hectare — single source of truth on the admin side. */
export const ACRES_PER_HECTARE = 2.47105;

export interface AgremoCreditBlock {
  id: string;
  totalCredits: number;
  remainingCredits: number;
  /**
   * Native unit for this block. Typed as optional because pre-2026-05-20
   * Hub builds don't include the field on the wire. In practice, code
   * reading these blocks should always see a defined value:
   * `AgremoAdminService.listCredits()` normalizes missing creditType to
   * `'analysis'` (the legacy default) before handing blocks to the rest
   * of the app, so direct-from-API consumers stay agnostic to the Hub
   * version they're talking to.
   */
  creditType?: AgremoCreditType;
  expiresAt: string;
  organizationId: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  archivedBy: string | null;
}

/**
 * Two valid wire shapes — modeled as a union with `?: never` exclusions so
 * a caller can't accidentally send `{ expiresAt }` alone (no quantity) or
 * both `totalCredits` AND `amount` (ambiguous unit). Both forks are kept
 * because the Hub still accepts the legacy shape post-2026-05-20 migration
 * — see /admin/agremo/credits handler — but admin UI callers should
 * always use the new shape going forward.
 */
interface AgremoCreditBlockCreateRequestBase {
  expiresAt: string;
  organizationId?: string | null;
}

/** New shape — preferred. Amount expressed in `creditType`'s native unit. */
export interface AgremoCreditBlockCreateRequestNew extends AgremoCreditBlockCreateRequestBase {
  amount: number;
  creditType: AgremoCreditType;
  totalCredits?: never;
}

/** Legacy shape (still accepted by the Hub, interpreted as acres). */
export interface AgremoCreditBlockCreateRequestLegacy extends AgremoCreditBlockCreateRequestBase {
  totalCredits: number;
  amount?: never;
  creditType?: never;
}

export type AgremoCreditBlockCreateRequest =
  | AgremoCreditBlockCreateRequestNew
  | AgremoCreditBlockCreateRequestLegacy;

export interface AgremoCreditBlockUpdateRequest {
  totalCredits?: number;
  expiresAt?: string;
}

// ============================================
// ANALYSIS
// ============================================

export type AgremoAnalysisStatus = 'pending' | 'queued' | 'processing' | 'downloading' | 'completed' | 'failed';

export interface AgremoAnalysis {
  id: string;
  organizationId: string;
  organizationName?: string;
  mapId: string;
  agremoAnalysisId: string | null;
  analysisTypeId: string | null;
  analysisTypeName?: string | null;
  status: AgremoAnalysisStatus;
  progress: number | null;
  requestedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  creditsConsumed: number;
  fieldName?: string;
  fieldId?: string;
  analysisType?: string;
  analysisParams?: any;
  statistics?: AgremoStatistics;
  createdAt: string;
  updatedAt: string;
}

export interface AgremoAnalysisFile {
  id: string;
  analysisId: string;
  fileType: string;
  agremoUrl: string | null;
  adnUrl: string | null;
  downloadStatus: string;
  downloadedAt: string | null;
  downloadUrl: string | null;
}

export type AgremoMapStatus = 'uploading' | 'processing' | 'ready' | 'failed';

export interface AgremoAnalysisDetail extends AgremoAnalysis {
  mapStatus: AgremoMapStatus | null;
  mapName: string | null;
  files: AgremoAnalysisFile[];
}

// ============================================
// ANALYSIS STATISTICS (from Agremo API results)
// ============================================

export interface AgremoStatisticsGeneral {
  general_area: number;
  general_label: string;
  general_area_unit: string;
  general_percentage: number;
  general_stress_area: number;
}

export interface AgremoStatisticsCluster {
  id: number;
  area: number;
  color: string;
  label: string;
  area_unit: string;
  percentage: number;
}

export interface AgremoStatistics {
  general?: AgremoStatisticsGeneral;
  clusters?: AgremoStatisticsCluster[];
  [key: string]: unknown;
}

// ============================================
// USAGE STATISTICS
// ============================================

export interface AgremoUsageStats {
  totalConsumed: number;
  totalAvailable: number;
  /** Total acres actually analyzed (sum of agremo_analyses.billed_acres). */
  totalAcresAnalyzed?: number;
  byOrganization: AgremoOrgUsage[];
  byAnalysisType: AgremoTypeUsage[];
}

export interface AgremoOrgUsage {
  organizationId: string;
  organizationName: string;
  /** Legacy raw sum of usage.credits_consumed — mixed-unit post-migration. */
  creditsConsumed: number;
  /** Sum of analyses.billed_acres for this org (NULL/legacy rows count as 0). */
  acresAnalyzed?: number;
  analysisCount: number;
}

export interface AgremoTypeUsage {
  analysisType: string;
  analysisTypeName?: string | null;
  /** Legacy raw sum of usage.credits_consumed — mixed-unit post-migration. */
  creditsConsumed: number;
  /** Sum of analyses.billed_acres for this analysis type. */
  acresAnalyzed?: number;
  analysisCount: number;
}

// ============================================
// HEALTH CHECK
// ============================================

export interface AgremoHealthStatus {
  configured?: boolean;
  connected: boolean;
  accountInfo?: Record<string, unknown>;
  error?: string;
  checkedAt: string;
}

// ============================================
// MAP STATUS CODEBOOK
// ============================================

export interface AgremoMapStatusCode {
  status_code: number;
  status_description: string;
}

export interface AgremoCameraType {
  camera_code: number;
  camera_bands: string;
}

export interface AgremoLayerType {
  layer_type_code: number;
  layer_type_description: string;
}

export interface AgremoLayerStatusCode {
  status_code: number;
  status_description: string;
}

export interface AgremoAnalysisTypeCodebook {
  id: number;
  name: string;
}

// ============================================
// PROCESS PENDING
// ============================================

export interface AgremoProcessPendingResult {
  processed: number;
  failed: number;
  remaining: number;
}

// ============================================
// LIST FILTERS
// ============================================

export interface AgremoAnalysisFilters {
  page?: number;
  limit?: number;
  organizationId?: string;
  status?: AgremoAnalysisStatus | 'all';
  startDate?: string;
  endDate?: string;
}

// ============================================
// PAGINATED RESPONSES
// ============================================

export interface PaginatedCreditsResponse {
  credits: AgremoCreditBlock[];
  /** Legacy field — sum of remaining_credits across all units. Prefer the per-unit values below. */
  totalAvailable: number;
  /** Acre-equivalent of remaining `acres` + `hectares` blocks. */
  availableAcres?: number;
  /** Same value expressed in hectares (for the Agremo-matching display). */
  availableHectares?: number;
  /** Count of remaining legacy `analysis` credits. */
  availableAnalyses?: number;
  expiringBlockCount: number;
}

export interface PaginatedAnalysesResponse {
  analyses: AgremoAnalysis[];
  total: number;
  page: number;
  pageSize: number;
}
